import { lazy } from 'react';

import {
  FormPageStepProps,
  RestoredStep01Data,
  RestoredStep01ExternalData,
} from '@features/campaigns/builder/containers/FormPages/FormPages.types.ts';
import { CampaignTypes } from '@features/campaigns/shared/types/campaign.ts';
import { useUserProfileByIdData } from '@shared/hooks/useUserProfileByIdData.ts';
import { useUserProfileData } from '@shared/hooks/useUserProfileData.ts';
import { FeatureFlagPossibleState } from '@shared/providers/FeatureFlagsProvider/providerSetup.ts';
import { useFeatureFlags } from '@shared/providers/FeatureFlagsProvider/useFeatureFlags.tsx';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { PossibleOnboardingState } from '@shared/types/userProfile.types.ts';

const Step01 = lazy(() => import('@features/campaigns/builder/containers/FormPages/Step01/Step01.tsx'));
const Step01External = lazy(
  () => import('@features/campaigns/builder/containers/FormPages/Step01External/Step01External.tsx'),
);

type Step01Props = FormPageStepProps<RestoredStep01Data>;
type Step01ExternalProps = FormPageStepProps<RestoredStep01ExternalData> & {
  isOnboardingCompleted: boolean;
  onboardingStatus: PossibleOnboardingState;
};

export function FormPagesStep01(props: Partial<Step01Props & Step01ExternalProps>) {
  const { isExternalDirectClient, isExternalAgency } = useUserDetails();
  const { FeatureFlag_Step01_External } = useFeatureFlags();
  const isCurrentUserExternal = isExternalDirectClient || isExternalAgency;
  const shouldDisplayStep01External = isCurrentUserExternal || props.campaignType === CampaignTypes.SELF_SERVICE;
  const allowedStep01ExternalView = FeatureFlag_Step01_External === FeatureFlagPossibleState.ACTIVE;

  const { isOnboardingCompleted: isExternalUserOnboardingCompletedForExternalUser, onboardingState } =
    useUserProfileData({ canFetchUserProfileData: isCurrentUserExternal && allowedStep01ExternalView });

  const canInternalUserGetExternalUserOnboardingStatus =
    !isCurrentUserExternal && props.campaignType === CampaignTypes.SELF_SERVICE && allowedStep01ExternalView;

  const { isOnboardingCompleted: isExternalUserOnboardingCompletedForInternalUser } = useUserProfileByIdData({
    id: props.stepSavedData?.createdBy || '',
    canFetchData: canInternalUserGetExternalUserOnboardingStatus,
  });

  const isOnboardingCompleted =
    isExternalUserOnboardingCompletedForExternalUser || isExternalUserOnboardingCompletedForInternalUser;

  return shouldDisplayStep01External && allowedStep01ExternalView ? (
    <Step01External isOnboardingCompleted={isOnboardingCompleted} {...props} onboardingStatus={onboardingState} />
  ) : (
    <Step01 {...props} />
  );
}
